import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import classes from '../pipeline.module.scss';
import Loader from '../../../../_common/Loader/Loader';

const TIP_ID = 'pipeline_id';

export const tipAnchor = (title, value, tooltip) => (tooltip
  ? {
    'data-tooltip-id': TIP_ID,
    'data-tip': JSON.stringify({ ...tooltip, title, value }),
  }
  : null);

const TipContent = ({ tip }) => (
  <div className={classes.tip_body}>
    <div className={classes.tip_head}>
      <h4>{tip.title}</h4>

      {tip.value !== undefined && tip.value !== null && (
        <span className={classes.tip_value}>{tip.value}</span>
      )}
    </div>

    <p className={classes.tip_text}>{tip.text}</p>

    {!!tip.steps?.length && (
      <>
        <p className={classes.tip_sub}>How it&apos;s worked out</p>

        <ul className={classes.tip_steps}>
          {tip.steps.map((step) => <li key={step}>{step}</li>)}
        </ul>
      </>
    )}

    {!!tip.note && (
      <p className={`${classes.tip_note} ${tip.warn ? classes.tip_note_warn : ''}`}>
        <span className={classes.tip_note_label}>{tip.warn ? 'Check this' : 'Worth knowing'}</span>

        {tip.note}
      </p>
    )}
  </div>
);

TipContent.propTypes = {
  tip: PropTypes.object.isRequired,
};

export const PipelineTooltip = () => (
  <Tooltip
    id={TIP_ID}
    className={classes.tip}
    place="bottom"
    offset={12}
    render={({ activeAnchor }) => {
      const tip = activeAnchor?.getAttribute('data-tip');

      return tip ? <TipContent tip={JSON.parse(tip)} /> : null;
    }}
  />
);

export const SectionHead = ({ section }) => (
  <div className={classes.section_head} id={section.id}>
    <span className={classes.section_num}>{section.num}</span>

    <h3>{section.title}</h3>
  </div>
);

SectionHead.propTypes = {
  section: PropTypes.object.isRequired,
};

export const Card = ({
  title = '', caption = '', className = '', bodyClassName = '', firstLoad, loading, isEmpty, children,
}) => (
  <div className={`${classes.card} ${className}`}>
    {!!title && <h4>{title}</h4>}

    {!!caption && <p className={classes.card_caption}>{caption}</p>}

    <div className={`${classes.card_body} ${loading ? classes.card_body_loading : ''} ${bodyClassName}`}>
      {loading && <Loader className={classes.card_loader} />}

      {!firstLoad && (isEmpty
        ? <div className={classes.card_is_empty}>No Data Found</div>
        : children)}
    </div>
  </div>
);

Card.propTypes = {
  title: PropTypes.string,
  caption: PropTypes.string,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  loading: PropTypes.bool,
  isEmpty: PropTypes.bool,
  children: PropTypes.node,
};

export const StatsCards = ({ cards, loading, firstLoad }) => (
  <div className={classes.stats_wrapper}>
    {loading && <Loader className={classes.stats_loading} />}

    {!firstLoad && cards.map((card) => (
      <div
        className={classes.stats}
        key={card.label}
        {...tipAnchor(card.label, card.value || 0, card.tooltip)}
      >
        <h3 className={classes.stats_value}>{card.value || 0}</h3>

        <p className={classes.stats_label}>{card.label}</p>
      </div>
    ))}
  </div>
);

StatsCards.propTypes = {
  cards: PropTypes.array.isRequired,
  loading: PropTypes.bool,
};

export const LineChart = ({
  data, scale = 'max', className = '', onClick,
}) => {
  const max = Math.max(...data.map((item) => item.value), 0);

  return (
    <div className={`${classes.bar_list} ${className}`}>
      {data.map((item) => (
        <div
          className={`${classes.bar_row} ${onClick ? classes.bar_row_clickable : ''}`}
          key={item.label || item.code}
          {...tipAnchor(item.label || item.code, item.caption || item.value, item.tooltip)}
          onClick={() => onClick?.(item)}
        >
          <div className={classes.bar_row_top}>
            <span>{item.code ? <code>{item.code}</code> : item.label}</span>

            <span className={classes.bar_row_value}>{item.caption || item.value}</span>
          </div>

          <div className={classes.bar_track}>
            <div
              className={`${classes.bar_fill} ${item.alt ? classes.bar_fill_alt : ''}`}
              style={{
                width: `${scale === 'percent' || !max ? (item.value || 0) : (item.value / max) * 100}%`,
              }}
            />
          </div>

          {!!item.desc && <p className={classes.bar_row_desc}>{item.desc}</p>}
        </div>
      ))}
    </div>
  );
};

LineChart.propTypes = {
  data: PropTypes.array.isRequired,
  scale: PropTypes.oneOf(['max', 'percent']),
  className: PropTypes.string,
  onClick: PropTypes.func,
};

export const BarChart = ({ data, className = '' }) => {
  const value = (item) => parseFloat(String(item.value).replace(/,/g, '')) || 0;

  const max = Math.max(...data.map(value), 0);

  return (
    <div className={`${classes.funnel} ${className}`}>
      {data.map((item, i) => (
        <div
          className={classes.funnel_row}
          key={item.label}
          {...tipAnchor(item.label, item.value, item.tooltip)}
        >
          <p className={classes.funnel_label}>{item.label}</p>

          <div
            className={`${classes.funnel_bar} ${classes[`funnel_bar_${Math.min(i + 1, 4)}`]}`}
            style={{ width: `${item.width || (max ? (value(item) / max) * 100 : 0)}%` }}
          >
            <span>{item.value}</span>

            {!!item.pct && <span className={classes.funnel_pct}>{item.pct}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

BarChart.propTypes = {
  data: PropTypes.array.isRequired,
  className: PropTypes.string,
};

export const Steps = ({ items, className = '' }) => (
  <div className={`${classes.steps} ${className}`}>
    {items.map((item, i) => (
      <div className={classes.step} key={i}>
        <span className={classes.step_num}>{i + 1}</span>

        <p className={classes.step_text}>{item}</p>
      </div>
    ))}
  </div>
);

Steps.propTypes = {
  items: PropTypes.array.isRequired,
  className: PropTypes.string,
};
